import { PasswordPolicy, Validator } from "@companion-core/shared/app/Interfaces/validators";

export const isRegexMatched = (regex: string, value: string) => {
  const regExp = new RegExp(regex);
  return regExp.test(value);
};

export const isString = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  } else {
    return !!(value && value.trim());
  }
};

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isEmail = (value: string): boolean => emailRegex.test(String(value).toLowerCase());

export const isStrongPassword = (policies: PasswordPolicy[], value: string): boolean =>
  policies.every((policy) => isRegexMatched(policy.regex, value));

const visaCreditCardRegex = /\b4\d{3}[ -]*\d{4}[ -]*\d{4}[ -]*\d(?:\d{3})?/;

export const isVisaCreditCard = (value: string): boolean => visaCreditCardRegex.test(String(value));

export const isArray = (data: unknown): boolean => Array.isArray(data);

export const isArrayEmpty = (data: unknown[]): boolean => data.length === 0;

export const isBoolean = (value: unknown): boolean => typeof value === "boolean";

export const isObject = (value: unknown): boolean => typeof value === "object" && value !== null;

export const isLastDigits = (value: Array<number>): boolean => value.length === 4;

export const isAtLeastFourCharLong = (value: string): boolean => {
  const matches = value.match(/[a-zA-Z0-9]/g);
  return !!(matches?.length && matches.length >= 4);
};

export const isAllHeartistsCardNumber = (value: string): boolean => /^[A-Z0-9 ]+$/g.test(value);

export const isAllHeartistsCardNumberOrEmail = (value: string): boolean =>
  isAllHeartistsCardNumber(value) || isEmail(value);

/**
 * Compare 2 versions to check the current version is up-to-date
 * Expects both arguments in a X.X.X format where X is a number
 *
 * @param {string} [current] Current version to test
 * @param {string} [target] Target version to match
 * @returns {boolean} The result of the comparison
 */
export function isVersionUpToDate(current: string, target: string): boolean {
  const currentVersionSplitted = current.split(".").map(Number);
  const targetVersionSplitted = target.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    if (i - 1 >= 0 && currentVersionSplitted[i - 1] > targetVersionSplitted[i - 1]) return true;
    if (currentVersionSplitted[i] < targetVersionSplitted[i]) return false;
  }
  return true;
}

export const validators = {
  isArray,
  isArrayEmpty,
  isBoolean,
  isEmail,
  isObject,
  isString,
  isStrongPassword,
  isVisaCreditCard,
  isLastDigits,
  isAtLeastFourCharLong,
  isAllHeartistsCardNumber,
  isAllHeartistsCardNumberOrEmail,
};

/**
 * Performs validation on a given value using an array of validators.
 *
 * @param arrayValidators - An array of validator functions.
 * @param valueToValidate - The value to be validated.
 *
 * @returns An object containing the validation result:
 *   - isValid: A boolean indicating if the value is valid or not.
 *   - errorMessages: An array of error messages, if any.
 */
export default function getValidation(
  arrayValidators: Array<Validator>,
  valueToValidate: string
): { isValid: boolean; errorMessages: string[] } {
  const result: { isValid: boolean; errorMessages: string[] } = {
    isValid: true,
    errorMessages: [],
  };

  arrayValidators.forEach((validator: Validator) => {
    const isValueValid: boolean = (validators[validator] as (value: string) => boolean)(
      valueToValidate
    );
    if (!isValueValid) {
      result.errorMessages.push(`common.form.input.validationErrors.${validator}`);
      result.isValid = false;
    }
  });

  return result;
}
